/**
 * Client Protected Route Component
 * Ensures only authenticated client users can access client routes.
 *
 * Cookie-based verification via GET /api/auth/verify-client.
 * Full-viewport loading shell avoids flash of admin layout content.
 */

'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, Alert, Button } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { AuthPendingScreen } from '@/shared/components/feedback/AuthPendingScreen';
import { AppLoadingScreen } from '@/shared/components/feedback/AppLoadingScreen';
import {
  clientLoginHref,
  forceClientSessionExpiredSignOut,
  isSigningOut,
} from '@/domains/auth/utils/sign-out';
import {
  cacheVerifiedClient,
  clearCachedClient,
  readCachedClient,
  type VerifiedClientUser,
} from '@/domains/auth/utils/client-auth-cache';
import { isClientUser } from '../../utils/user-display';

const JUST_LOGGED_IN_KEY = 'mechagora:just_logged_in';
const JUST_LOGGED_IN_TTL_MS = 15_000;
const VERIFY_TIMEOUT_MS = 8_000;
const VERIFY_FEEDBACK_DELAY_MS = 180;

let handoffVerificationRequest: Promise<Response> | null = null;

function verifyCachedHandoff(): Promise<Response> {
  if (!handoffVerificationRequest) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);

    handoffVerificationRequest = fetch('/api/auth/verify-client', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    }).finally(() => {
      window.clearTimeout(timeout);
      handoffVerificationRequest = null;
    });
  }

  return handoffVerificationRequest.then((response) => response.clone());
}

function consumeJustLoggedIn(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(JUST_LOGGED_IN_KEY);
    if (!raw) return false;
    const t = Number(raw);
    const fresh = Number.isFinite(t) && Date.now() - t >= 0 && Date.now() - t < JUST_LOGGED_IN_TTL_MS;
    // Keep flag until verify succeeds so first paint stays "opening workspace"
    return fresh;
  } catch {
    return false;
  }
}

function clearJustLoggedIn(): void {
  try {
    sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
  } catch {
    /* no-op */
  }
}

interface ClientProtectedRouteProps {
  children: React.ReactNode;
}

type GuardPhase = 'verifying' | 'redirecting' | 'error' | 'denied' | 'ready';

const shellStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100dvh',
  width: '100%',
  background: 'var(--shell-content-bg, var(--color-background, #E0D2C2))',
  padding: 24,
  boxSizing: 'border-box',
};

const cardStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '24px 28px',
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 1px 2px rgba(42,31,26,0.05), 0 8px 24px rgba(42,31,26,0.06)',
  borderRadius: 16,
  background: 'var(--shell-workspace-bg, #FBF6F0)',
  border: '1px solid var(--shell-content-edge, #C9B49E)',
};

const pendingShellStyle: React.CSSProperties = {
  minHeight: 320,
  width: '100%',
};

const ClientAuthContext = createContext<{ user: VerifiedClientUser } | null>(null);

const ClientProtectedRoute: React.FC<ClientProtectedRouteProps> = ({ children }) => {
  const { replace } = useRouter();
  const [cachedUser, setCachedUser] = useState<VerifiedClientUser | null>(null);
  const [user, setUser] = useState<VerifiedClientUser | null>(null);
  const [phase, setPhase] = useState<GuardPhase>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [redirectMessage, setRedirectMessage] = useState<string | null>(null);
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [showVerificationFeedback, setShowVerificationFeedback] = useState(false);
  const [verifyTimedOut, setVerifyTimedOut] = useState(false);
  // Storage-backed auth hints are loaded only after hydration so SSR and the
  // first client render are deterministic.
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const cached = readCachedClient();
    const loginHandoff = consumeJustLoggedIn();
    setCachedUser(cached);
    setUser(cached);
    setPhase(cached ? 'ready' : 'verifying');
    setJustLoggedIn(loginHandoff);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (phase !== 'verifying' || justLoggedIn) {
      setShowVerificationFeedback(false);
      return;
    }

    const timer = window.setTimeout(
      () => setShowVerificationFeedback(true),
      VERIFY_FEEDBACK_DELAY_MS
    );
    return () => window.clearTimeout(timer);
  }, [phase, justLoggedIn]);

  useEffect(() => {
    if (phase !== 'verifying') {
      setVerifyTimedOut(false);
      return;
    }
    const id = window.setTimeout(() => setVerifyTimedOut(true), VERIFY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    const onTimeout = () => setVerifyTimedOut(true);
    window.addEventListener('mechagora:auth-pending-timeout', onTimeout);
    return () => window.removeEventListener('mechagora:auth-pending-timeout', onTimeout);
  }, []);

  useEffect(() => {
    if (!hydrated) return;

    let cancelled = false;

    // Sign-out owns exit — do not verify-client or soft-redirect during teardown
    if (isSigningOut()) {
      return;
    }

    const controller = cachedUser ? null : new AbortController();
    const timeout = controller
      ? window.setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS)
      : null;

    const redirectToLogin = (reason: string, _message: string) => {
      if (cancelled || isSigningOut()) return;
      clearCachedClient();
      clearJustLoggedIn();
      setUser(null);
      setRedirectMessage(
        reason === 'session_expired'
          ? 'Your session expired. Signing you out…'
          : 'Sign in required.'
      );
      setPhase('redirecting');
      // Bank-speed: clear cookies + hard nav with return_to (not soft router.replace)
      const returnTo =
        typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search || ''}`
          : '/client/dashboard';
      forceClientSessionExpiredSignOut({
        returnTo,
        error: reason || 'session_expired',
        skipIfPublic: false,
      });
    };

    const verifyClientAuth = async () => {
      if (isSigningOut()) return;
      const attempt = async (): Promise<Response> =>
        cachedUser
          ? verifyCachedHandoff()
          : fetch('/api/auth/verify-client', {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller?.signal,
          });

      try {
        // Cookies are sent automatically; no localStorage checks.
        // One retry covers post-login cookie race / transient JWKS cold start.
        let response = await attempt();
        if (!response.ok && response.status >= 500) {
          await new Promise((r) => setTimeout(r, 250));
          if (cancelled) return;
          response = await attempt();
        } else if (response.status === 401 && justLoggedIn) {
          // The server already retries verification; only a fresh login gets one
          // final browser-side allowance for the Set-Cookie navigation handoff.
          await new Promise((r) => setTimeout(r, 80));
          if (cancelled) return;
          response = await attempt();
        }

        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();

          if (data.success && data.user && isClientUser(data.user)) {
            clearJustLoggedIn();
            cacheVerifiedClient(data.user);
            setUser(data.user);
            setPhase('ready');
            return;
          }

          // Authenticated but not a client — do not flash admin shell
          redirectToLogin(
            'invalid_user_type',
            'This area is for client accounts only. Redirecting to sign in…'
          );
          return;
        }

        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const isUnauthorized = response.status === 401 || response.status === 403;
        redirectToLogin(
          isUnauthorized ? 'session_expired' : 'verification_failed',
          isUnauthorized
            ? 'Your session has expired. Redirecting to sign in…'
            : (errorData?.error as string) || 'Could not verify client access. Redirecting…'
        );
      } catch (verificationError) {
        if (cancelled) return;
        if (
          verificationError instanceof DOMException &&
          verificationError.name === 'AbortError'
        ) {
          clearCachedClient();
          setUser(null);
          setError('Session verification timed out. Check your connection and try again.');
          setPhase('error');
          return;
        }
        // Network blip — one more try before error UI
        try {
          await new Promise((r) => setTimeout(r, 300));
          if (cancelled) return;
          const response = await attempt();
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user && isClientUser(data.user)) {
              clearJustLoggedIn();
              cacheVerifiedClient(data.user);
              setUser(data.user);
              setPhase('ready');
              return;
            }
          }
        } catch {
          // fall through
        }
        if (cancelled) return;
        clearCachedClient();
        setUser(null);
        setError('Failed to verify authentication. Please try again.');
        setPhase('error');
      }
    };

    void verifyClientAuth().finally(() => {
      if (timeout) window.clearTimeout(timeout);
    });

    return () => {
      cancelled = true;
      controller?.abort();
      if (timeout) window.clearTimeout(timeout);
    };
  }, [replace, justLoggedIn, verificationAttempt, cachedUser, hydrated]);

  const retryVerification = () => {
    clearCachedClient();
    setUser(null);
    setError(null);
    setRedirectMessage(null);
    setPhase('verifying');
    setVerificationAttempt((attempt) => attempt + 1);
  };

  useEffect(() => {
    const onRetry = () => retryVerification();
    window.addEventListener('mechagora:auth-pending-retry', onRetry);
    return () => window.removeEventListener('mechagora:auth-pending-retry', onRetry);
  }, []);

  const goToSignIn = () => {
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search || ''}`
        : '/client/dashboard';
    replace(
      clientLoginHref({
        error: 'session_expired',
        returnTo,
      })
    );
  };

  // Sign-out: shell owns SignOutScreen — avoid stacked panels
  if (isSigningOut()) {
    return null;
  }

  // Cookie verify — never render protected descendants without an auth context.
  // A verified cache can keep route handoffs instant; cold/direct loads wait for
  // cookie verification so useClientAuth consumers never render outside the provider.
  if (phase === 'verifying') {
    const fallbackUser = user ?? cachedUser;
    if (fallbackUser) {
      return (
        <ClientAuthContext.Provider value={{ user: fallbackUser }}>
          {children}
        </ClientAuthContext.Provider>
      );
    }

    if (justLoggedIn) {
      return (
        <AppLoadingScreen
          message="Opening your workspace…"
          variant="fullscreen"
          size="lg"
        />
      );
    }

    if (!hydrated || !showVerificationFeedback) {
      return (
        <div
          style={pendingShellStyle}
          role="status"
          aria-label="Verifying client session"
          aria-busy="true"
        />
      );
    }

    return (
      <AuthPendingScreen
        message={verifyTimedOut ? 'Session verification is taking longer than expected…' : undefined}
        phase="verify"
        variant="workspace"
      />
    );
  }

  // Auth tier: leave protected area — session-oriented copy, not "Loading app"
  if (phase === 'redirecting') {
    return (
      <AuthPendingScreen
        message={redirectMessage || 'Redirecting…'}
        phase="redirect"
      />
    );
  }

  if (phase === 'error') {
    return (
      <div style={shellStyle}>
        <Card style={{ ...cardStyle, maxWidth: 480, textAlign: 'left' }} variant="borderless">
          <Alert
            title="Couldn’t verify your session"
            description={error || 'Failed to verify authentication.'}
            type="error"
            showIcon
            action={
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button type="primary" onClick={retryVerification}>
                  Try again
                </Button>
                <Button onClick={goToSignIn}>
                  Sign in
                </Button>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  if (phase === 'denied' || !user) {
    return (
      <div style={shellStyle}>
        <Card style={{ ...cardStyle, maxWidth: 480, textAlign: 'left' }} variant="borderless">
          <Alert
            title="Access Denied"
            description="You must be signed in as a client to access this page."
            type="warning"
            showIcon
            icon={<BankOutlined />}
            action={
              <Button type="primary" onClick={goToSignIn}>
                Sign in
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <ClientAuthContext.Provider value={{ user }}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientProtectedRoute');
  }
  return context;
};

export default ClientProtectedRoute;
